import React, { useState, useEffect } from "react";
import {
  FiSearch,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiSettings,
  FiRss,
  FiShoppingBag,
  FiEdit,
  FiImage,
  FiGlobe,
  FiMessageCircle
} from "react-icons/fi";
import AdminAutoPostSettings from "./AdminAutoPostSettings";

const AdminAutoSettings = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all', 'topic', 'rss', 'product', 'manual', 'photo', 'website', 'zalo'
  const [sortConfig, setSortConfig] = useState({ key: "total", direction: "desc" });
  const [selectedUserId, setSelectedUserId] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  const getAdminHeaders = () => {
    const token = localStorage.getItem("userAccessToken");
    const adminPassword =
      process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
    return {
      ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-settings`, {
        headers: getAdminHeaders(),
      });

      const result = await response.json();
      if (result.success) {
        setUsers(result.data || []);
      } else {
        console.error("Failed to fetch settings:", result.error);
        setUsers([]);
      }
    } catch (apiError) {
      console.error("Error fetching settings from API:", apiError);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aValue, bValue;

    if (sortConfig.key === "total") {
      aValue = Object.values(a.settings).reduce((sum, val) => sum + val, 0);
      bValue = Object.values(b.settings).reduce((sum, val) => sum + val, 0);
    } else if (sortConfig.key === "name") {
      aValue = a.name.toLowerCase();
      bValue = b.name.toLowerCase();
    } else if (["autoPostTopics", "rss", "productAutoPost", "manualPost", "photoList", "websitePost", "zaloPost"].includes(sortConfig.key)) {
      aValue = a.settings[sortConfig.key];
      bValue = b.settings[sortConfig.key];
    } else {
      aValue = a[sortConfig.key];
      bValue = b[sortConfig.key];
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredUsers = sortedUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
    if (!matchesSearch) return false;
    
    if (filterType === "all") return true;
    if (filterType === "topic") return user.settings.autoPostTopics > 0;
    if (filterType === "rss") return user.settings.rss > 0;
    if (filterType === "product") return user.settings.productAutoPost > 0;
    if (filterType === "manual") return user.settings.manualPost > 0;
    if (filterType === "photo") return user.settings.photoList > 0;
    if (filterType === "website") return user.settings.websitePost > 0;
    if (filterType === "zalo") return user.settings.zaloPost > 0;
    
    return true;
  });

  const SettingBadge = ({ count, icon: Icon, colorClass, title }) => {
    if (count === 0) return <span className="text-gray-300">-</span>;
    return (
      <div className={`flex items-center space-x-1 ${colorClass} font-medium`} title={title}>
        <Icon className="w-4 h-4" />
        <span>{count}</span>
      </div>
    );
  };

  if (selectedUserId) {
    return (
      <AdminAutoPostSettings
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
      />
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Quản Lý Cấu Hình Tự Động
          </h1>
          <p className="text-slate-500 mt-1">
            Danh sách người dùng và các thiết lập tự động của họ ({filteredUsers.length})
          </p>
        </div>
        
        <div className="flex gap-2">
           <button
            onClick={fetchSettings}
            className="flex items-center px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all font-medium shadow-sm"
          >
            <FiRefreshCw className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Làm mới
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
            <select
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-slate-700 font-medium cursor-pointer"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Tất cả loại</option>
              <option value="topic">Theo Chủ Đề</option>
              <option value="rss">RSS Articles</option>
              <option value="product">Sản Phẩm</option>
              <option value="manual">Thủ Công</option>
              <option value="photo">DS Ảnh</option>
              <option value="website">Website</option>
              <option value="zalo">Zalo OA</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort("name")}>
                  <div className="flex items-center">
                    User Info
                    {sortConfig.key === "name" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("autoPostTopics")}>
                  <div className="flex items-center justify-center" title="Auto Post from Topics">
                    <FiSettings className="mr-1" /> Topics
                    {sortConfig.key === "autoPostTopics" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("rss")}>
                  <div className="flex items-center justify-center" title="Auto Post from RSS">
                    <FiRss className="mr-1" /> RSS
                    {sortConfig.key === "rss" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("productAutoPost")}>
                  <div className="flex items-center justify-center" title="Auto Post from Products">
                    <FiShoppingBag className="mr-1" /> Products
                    {sortConfig.key === "productAutoPost" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("manualPost")}>
                  <div className="flex items-center justify-center" title="Scheduled Manual Posts">
                    <FiEdit className="mr-1" /> Manual
                    {sortConfig.key === "manualPost" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("photoList")}>
                  <div className="flex items-center justify-center" title="Auto Post from Photo List">
                    <FiImage className="mr-1" /> Photos
                    {sortConfig.key === "photoList" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("websitePost")}>
                  <div className="flex items-center justify-center" title="Auto Post from Website">
                    <FiGlobe className="mr-1" /> Website
                    {sortConfig.key === "websitePost" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("zaloPost")}>
                  <div className="flex items-center justify-center" title="Auto Post Zalo OA">
                    <FiMessageCircle className="mr-1" /> Zalo
                    {sortConfig.key === "zaloPost" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors text-center" onClick={() => handleSort("total")}>
                   <div className="flex items-center justify-center">
                    Total
                    {sortConfig.key === "total" && (sortConfig.direction === "asc" ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />)}
                  </div>
                </th>
                <th className="px-6 py-4 text-center">
                  Chi tiết
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center text-slate-500">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-2"></div>
                      Đang tải dữ liệu...
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center text-slate-500">
                    Không tìm thấy dữ liệu phù hợp
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.userId}
                    className="hover:bg-slate-50 transition-colors group cursor-pointer"
                    onClick={() => setSelectedUserId(user.userId)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-600 font-bold text-sm mr-3 border border-blue-200">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                            {user.name}
                          </div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                          {user.membership && (
                             <div className="text-[10px] inline-block px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 mt-1 border border-yellow-200">
                                {user.membership.planId ? `Plan: ${user.membership.planId}` : 'Member'}
                             </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <SettingBadge 
                          count={user.settings.autoPostTopics} 
                          icon={FiSettings} 
                          colorClass="text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" 
                          title="Auto Post Topics"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.rss} 
                          icon={FiRss} 
                          colorClass="text-orange-600 bg-orange-50 px-2 py-1 rounded-md border border-orange-100" 
                          title="RSS Feeds"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.productAutoPost} 
                          icon={FiShoppingBag} 
                          colorClass="text-green-600 bg-green-50 px-2 py-1 rounded-md border border-green-100" 
                          title="Product Auto Post"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.manualPost} 
                          icon={FiEdit} 
                          colorClass="text-blue-600 bg-blue-50 px-2 py-1 rounded-md border border-blue-100" 
                          title="Manual Scheduled Posts"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.photoList} 
                          icon={FiImage} 
                          colorClass="text-pink-600 bg-pink-50 px-2 py-1 rounded-md border border-pink-100" 
                          title="Photo List Posts"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.websitePost} 
                          icon={FiGlobe} 
                          colorClass="text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md border border-indigo-100" 
                          title="Website Posts"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                         <SettingBadge 
                          count={user.settings.zaloPost} 
                          icon={FiMessageCircle} 
                          colorClass="text-cyan-600 bg-cyan-50 px-2 py-1 rounded-md border border-cyan-100" 
                          title="Zalo OA Posts"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold text-sm border border-slate-200">
                        {Object.values(user.settings).reduce((a, b) => a + b, 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUserId(user.userId);
                        }}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors"
                      >
                        Xem đầy đủ
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 text-xs text-slate-500 flex justify-between items-center">
           <div>
             Hiển thị {filteredUsers.length} / {users.length} người dùng
           </div>
           <div>
             AutoWork Admin Panel
           </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAutoSettings;
